import { apiClient } from './api.js';

export type SiteMode = 'commercial_multi_tenant' | 'single_tenant' | 'multi_family';

export interface SetupStatus {
  completed: boolean;
  site_name: string;
  /** Product mode chosen at setup; drives nav, vocabulary, dashboards. */
  site_mode: SiteMode;
  has_master: boolean;
  has_unifi: boolean;
  webhook_callback_url: string;
  requires_setup_token: boolean;
  mfa_enforced: boolean;
}

export function fetchSetupStatus(): Promise<SetupStatus> {
  return apiClient
    .get<{ data: SetupStatus }>('/api/v1/setup/status')
    .then((r) => r.data);
}

export interface ProbeResult {
  ok: boolean;
  controller_version: string;
  uptime_seconds: number;
  api_path: '/api/v1/developer';
  capabilities: {
    supports_webhooks: 'supported' | 'unsupported' | 'missing_permission' | 'unknown';
    supports_visitors: 'supported' | 'unsupported' | 'missing_permission' | 'unknown';
    supports_touch_passes: 'supported' | 'unsupported' | 'missing_permission' | 'unknown';
    supports_license_plates: 'supported' | 'unsupported' | 'missing_permission' | 'unknown';
    supports_door_lock_rules: 'supported' | 'unsupported' | 'missing_permission' | 'unknown';
    supports_device_settings: 'supported' | 'unsupported' | 'missing_permission' | 'unknown';
    supports_access_policies: 'supported' | 'unsupported' | 'missing_permission' | 'unknown';
    supports_user_groups: 'supported' | 'unsupported' | 'missing_permission' | 'unknown';
    supports_system_logs: 'supported' | 'unsupported' | 'missing_permission' | 'unknown';
  };
}

export function probeUnifi(
  input: {
    base_url: string;
    api_key: string;
    tls_reject_unauthorized: boolean;
  },
  opts: { setupToken?: string } = {},
): Promise<ProbeResult> {
  return apiClient
    .post<{ data: ProbeResult }>('/api/v1/setup/probe-unifi', input, {
      headers: opts.setupToken ? { 'x-setup-token': opts.setupToken } : undefined,
    })
    .then((r) => r.data);
}

export interface InitResult {
  ok: boolean;
  webhook_callback_url: string;
  webhook_hmac_secret?: string;
  sign_in_url: string;
  /** Returned when MFA is enforced — the wizard must walk the operator through enrollment. */
  mfa?: {
    secret: string;
    otpauth_url: string;
    qr_png_data_url: string;
    bootstrap_token: string;
  };
}

export function initSetup(
  input: {
    site_name: string;
    site_mode: SiteMode;
    master: { email: string; name: string; password: string };
    unifi: {
      base_url: string;
      api_key: string;
      tls_reject_unauthorized: boolean;
    };
    webhook_hmac_secret?: string;
  },
  opts: { setupToken?: string } = {},
): Promise<InitResult> {
  return apiClient
    .post<{ data: InitResult }>('/api/v1/setup/init', input, {
      headers: opts.setupToken ? { 'x-setup-token': opts.setupToken } : undefined,
    })
    .then((r) => r.data);
}

/** Complete first-master MFA bootstrap by verifying the TOTP. */
export function completeMfaBootstrap(input: {
  bootstrap_token: string;
  otp: string;
}): Promise<{ ok: true }> {
  return apiClient
    .post<{ data: { ok: true } }>(
      '/api/v1/setup/mfa-bootstrap',
      { otp: input.otp },
      { headers: { 'x-mfa-bootstrap-token': input.bootstrap_token } },
    )
    .then((r) => r.data);
}

/**
 * Pre-auth authenticator fallback enrollment for privileged users
 * blocked from session creation by login. Passkey setup is the default;
 * this two-call start -> verify flow is used when the device cannot
 * enroll a passkey.
 */
export interface MfaSetupStartResult {
  secret: string;
  otpauth_url: string;
  qr_png_data_url: string;
}

export function mfaSetupStart(setupToken: string): Promise<MfaSetupStartResult> {
  return apiClient
    .post<{ data: MfaSetupStartResult }>(
      '/api/v1/auth/mfa/setup/start',
      {},
      { headers: { 'x-mfa-setup-token': setupToken } },
    )
    .then((r) => r.data);
}

export function mfaSetupVerify(input: {
  setup_token: string;
  otp: string;
}): Promise<{ ok: true }> {
  return apiClient
    .post<{ data: { ok: true } }>(
      '/api/v1/auth/mfa/setup/verify',
      { otp: input.otp },
      { headers: { 'x-mfa-setup-token': input.setup_token } },
    )
    .then((r) => r.data);
}
